import { useQuery } from '@tanstack/react-query'
import dayjs from 'dayjs'
import { useCallback, useMemo, useState } from 'react'

import { toDecimalYear } from '@/pages/ui/utils'

import { getUserQuery } from '@/lib/queries/business/v2/user/queries'
import { getCaseExtraInfoQuery } from '@/lib/queries/business/v3/case-extra/queries'
import { getCaseProcessingTimeTrendQuery } from '@/lib/queries/case/public/trend/processing-time/queries'

import { useProcessingTimeTrendDialogStore } from '../../../check-uscis-case/stores/use-processing-time-trend-dialog-store'
import { useFindTargetCase } from '../../../hooks/use-find-target-case'
import { TIMEFRAME_OPTIONS } from '../processing-time-trend-dialog/constants'
import type { ProcessingTimeTrendMonthlySeriesItem } from '../processing-time-trend-dialog/types'

export const useProcessingTimeTrendDialog = () => {
  const { isDialogOpen, closeDialog } = useProcessingTimeTrendDialogStore()
  const { targetCase } = useFindTargetCase()
  const { data: dataCaseExtraInfo } = useQuery(getCaseExtraInfoQuery(targetCase?.case_id))
  const { data: dataProcessingTrend } = useQuery(
    getCaseProcessingTimeTrendQuery({
      category: dataCaseExtraInfo?.categoryCode,
      office: dataCaseExtraInfo?.officeCode,
      date: dayjs()
        .subtract(parseInt(TIMEFRAME_OPTIONS[TIMEFRAME_OPTIONS.length - 1].value, 10) + 1, 'months')
        .startOf('month')
        .format('YYYY-MM-DD'),
    })
  )
  const { data: dataUser } = useQuery(getUserQuery)

  const [timeFrameProcessingTime, setTimeFrameProcessingTime] = useState(
    TIMEFRAME_OPTIONS[TIMEFRAME_OPTIONS.length - 1].value
  )

  const [timeFrameInquiryDate, setTimeFrameInquiryDate] = useState(
    TIMEFRAME_OPTIONS[TIMEFRAME_OPTIONS.length - 1].value
  )

  const monthlySeries = useMemo(() => {
    if (!dataProcessingTrend?.processingTime) {
      return null
    }

    const monthlySeries0 = dataProcessingTrend.processingTime.map((item) => {
      return {
        ...item,
        caseInquiryDateDecimalYear: item.caseInquiryDate ? toDecimalYear(item.caseInquiryDate) : null,
      }
    })

    return normalizeMonthlySeries(monthlySeries0)
  }, [dataProcessingTrend])

  const monthlySeriesProcessingTime = useMemo(() => {
    if (!monthlySeries) {
      return null
    }

    return monthlySeries.slice(-parseInt(timeFrameProcessingTime, 10))
  }, [monthlySeries, timeFrameProcessingTime])

  const monthlySeriesInquiryDate = useMemo(() => {
    if (!monthlySeries) {
      return null
    }

    return monthlySeries.slice(-parseInt(timeFrameInquiryDate, 10))
  }, [monthlySeries, timeFrameInquiryDate])

  const applicationFilingDate = dataCaseExtraInfo?.applicationFiledDate

  const handleTimeFrameProcessingTimeChange = setTimeFrameProcessingTime
  const handleTimeFrameInquiryDateChange = setTimeFrameInquiryDate

  const handleChangeDialogOpen = useCallback(
    (open: boolean) => {
      if (open) {
        return
      }

      closeDialog()
    },
    [closeDialog]
  )

  return {
    dataUser,
    applicationFilingDate,
    monthlySeriesProcessingTime,
    monthlySeriesInquiryDate,
    timeFrameProcessingTime,
    timeFrameInquiryDate,
    isDialogOpen,
    handleChangeDialogOpen,
    handleTimeFrameProcessingTimeChange,
    handleTimeFrameInquiryDateChange,
  }
}

/**
 * Normalizes monthly series by filling missing months with placeholder values.
 *
 * When requesting N data points from the server, it may return fewer than N
 * points or there may be gaps between the months. In such cases, the missing
 * months are filled with placeholder values.
 */
const normalizeMonthlySeries = (data: ProcessingTimeTrendMonthlySeriesItem[]) => {
  const length = parseInt(TIMEFRAME_OPTIONS[TIMEFRAME_OPTIONS.length - 1].value, 10)

  // Sorted by date.
  const sorted = [...data].sort((a, b) => dayjs(a.date).diff(dayjs(b.date)))

  // Filled with placeholder values for missing months.
  const filled = fillMissingMonths(sorted)

  // Padded with leading placeholder values.
  const padded = ensureMonthlySeriesLength(length, filled)

  return padded
}

/**
 * Fills missing months with placeholder values.
 */
const fillMissingMonths = (data: ProcessingTimeTrendMonthlySeriesItem[]) => {
  if (data.length === 0) {
    return []
  }

  const filled: ProcessingTimeTrendMonthlySeriesItem[] = []

  for (let i = 0; i < data.length; i++) {
    const item = data[i]
    const nextItem = data[i + 1]

    filled.push(item)

    if (!nextItem) {
      break
    }

    const date0 = dayjs(item.date)
    const date1 = dayjs(nextItem.date)
    const monthDiff = date1.diff(date0, 'month')

    for (let j = 1; j < monthDiff; j++) {
      filled.push({
        caseInquiryDateDecimalYear: null,
        caseInquiryDate: null,
        estimatedTime: null,
        date: date0.add(j, 'month').format('YYYY-MM-DD'),
      })
    }
  }

  return filled
}

/**
 * Ensures the monthly series has the specified length by padding with leading
 * placeholder values.
 */
const ensureMonthlySeriesLength = (length: number, data: ProcessingTimeTrendMonthlySeriesItem[]) => {
  if (data.length >= length) {
    return data.slice(-length)
  }

  const padLength = Math.max(0, length - data.length)
  const result: ProcessingTimeTrendMonthlySeriesItem[] = [...data]

  if (result.length === 0) {
    result.push({
      estimatedTime: null,
      caseInquiryDate: null,
      caseInquiryDateDecimalYear: null,
      date: dayjs().startOf('month').format('YYYY-MM-DD'),
    })
  }

  for (let i = 0; i < padLength; i++) {
    const earliest = dayjs(result[0].date)
    const date = earliest.subtract(1, 'month')

    result.unshift({
      estimatedTime: null,
      caseInquiryDate: null,
      caseInquiryDateDecimalYear: null,
      date: date.format('YYYY-MM-DD'),
    })
  }

  return result
}
